import {ChangeDetectorRef, Component, Input, OnInit, ViewChild} from '@angular/core';
import {
  columnsAvailableProviders,
  columnsSelectedProviders,
  OntologyListData,
  OntologyListEntity,
  RelatableSelectableItem,
  ScenarioForDisplayability,
  UpdatedFilterTab
} from '../FilterBannerModels';
import {MatTableDataSource} from '@angular/material/table';
import {calculateSelectedNodePath, isItemAbleToShow, searchTextMinimum} from '../UpdatedFilterBannerHelpers';
import {SortingOrder} from '../../shared/enums';
import {SearchDepartmentComponent} from './search-department/search-department.component';
import {SearchSpecialtyComponent} from './search-specialty/search-specialty.component';
import {UpdatedSaveGroupingComponent} from '../updated-save-grouping/updated-save-grouping.component';
import {filterBannerEditChangedTo, filterCriteriaChangedTo} from '../../store/actions';
import {FilterCriteria, IAppState} from '../../store/IAppState';
import {MatDialog} from '@angular/material/dialog';
import {NgRedux} from '@angular-redux/store';
import {UpdatedFilterBannerComponent} from '../updated-filter-banner.component';
import {CustomGroupsDataAllFilters} from '../../shared/models';

@Component({
  selector: 'app-search-provider',
  templateUrl: './search-provider.component.html',
  styleUrls: ['./search-provider.component.scss']
})
export class SearchProviderComponent implements OnInit {

  @Input() ontologyListData: OntologyListData | undefined = undefined;
  @Input() onProvidersChanged: (tab?: UpdatedFilterTab) => void;
  @Input() customGroups: CustomGroupsDataAllFilters[] = [];
  @Input() selectedGroup: CustomGroupsDataAllFilters | undefined = undefined;
  @Input() filterTab: UpdatedFilterTab;
  @Input() parentApply: (component?: UpdatedFilterBannerComponent, filters?: FilterCriteria) => void;
  @Input() parent: UpdatedFilterBannerComponent;
  @ViewChild(SearchDepartmentComponent) searchDepartmentComponent: SearchDepartmentComponent;
  @ViewChild(SearchSpecialtyComponent) searchSpecialtyComponent: SearchSpecialtyComponent;
  defaultGroup: CustomGroupsDataAllFilters | undefined = undefined;
  groupIsEdited = false;
  showGroupings = false;
  listOfAllDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  listOfAllSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  listOfAllProviders: RelatableSelectableItem<OntologyListEntity>[] = [];
  hiddenProviders: RelatableSelectableItem<OntologyListEntity>[] = [];
  availableProviders: RelatableSelectableItem<OntologyListEntity>[] = [];
  selectedProviders: RelatableSelectableItem<OntologyListEntity>[] = [];
  anyDepartmentsSelected = false;
  anySpecialtySelected = false;
  dataSourceAvailableProviders: MatTableDataSource<RelatableSelectableItem<OntologyListEntity>>;
  dataSourceSelectedProviders: MatTableDataSource<RelatableSelectableItem<OntologyListEntity>>;
  readonly columnsAvailableProviders = columnsAvailableProviders;
  readonly columnsSelectedProviders = columnsSelectedProviders;
  displayedColumnsAvailableProviders: string[] = columnsAvailableProviders.map(col => col.header);
  displayedColumnsSelectedProviders: string[] = columnsSelectedProviders.map(col => col.header);
  private NUMBER_AVAILABLE_PROVIDERS_TO_SHOW = 50;
  private NUMBER_SELECTED_PROVIDERS_TO_SHOW = 50;
  searchText = '';
  showDepartments = false;
  showSpecialties = false;
  showProviderGuidelines = false;
  sortDirectionAvailableProviders: SortingOrder | undefined = undefined;
  sortColumnAvailableProviders: string | undefined = undefined;
  sortDirectionSelectedProviders: SortingOrder | undefined = undefined;
  sortColumnSelectedProviders: string | undefined = undefined;

  private static isGroupEdited(filterTab: UpdatedFilterTab): boolean {
    return !!filterTab.newSelections && (filterTab.newSelections.selections > 0 ||
      filterTab.newSelections.deselections > 0);
  }

  constructor(protected cdr: ChangeDetectorRef,
              private ngRedux: NgRedux<IAppState>,
              private dialog: MatDialog) { }

  ngOnInit(): void {
    this.defaultGroup = this.customGroups.find(group => group.isDefault);
    this.searchDepartmentComponent?.ngOnInit();
    this.searchSpecialtyComponent?.ngOnInit();
    this.listOfAllDepartments = this.ontologyListData?.departments || [];
    this.listOfAllSpecialties = this.ontologyListData?.specialties || [];
    this.listOfAllProviders = this.ontologyListData?.providers || [];
    this.anyDepartmentsSelected = !!this.ontologyListData?.departments.find(d => d.item.selected);
    this.anySpecialtySelected = !!this.ontologyListData?.specialties.find(s => s.item.selected);
    this.filterProviders();
    this.initializeSelected();
    this.showProviders();
  }

  private resetDataSources(): void {
    this.dataSourceAvailableProviders = new MatTableDataSource<RelatableSelectableItem<OntologyListEntity>>
    (this.availableProviders.slice(0, this.NUMBER_AVAILABLE_PROVIDERS_TO_SHOW));
    this.dataSourceSelectedProviders = new MatTableDataSource<RelatableSelectableItem<OntologyListEntity>>
    (this.selectedProviders.slice(0, this.NUMBER_SELECTED_PROVIDERS_TO_SHOW));
  }

  private initializeSelected(): void {
    this.hiddenProviders = this.listOfAllProviders.filter(p => !p.item.selected);
    this.selectedProviders = this.listOfAllProviders.filter(p => p.item.selected);
    this.availableProviders = [];
  }

  private showProviders(): void {
    const nonSelectedProviders = this.availableProviders.concat(this.hiddenProviders);
    const scenario = this.searchText.length >= searchTextMinimum ?
      ScenarioForDisplayability.MatchSearchTextRegardless : ScenarioForDisplayability.Standard;
    this.availableProviders = nonSelectedProviders.filter(p => isItemAbleToShow(p, scenario));
    this.hiddenProviders = nonSelectedProviders.filter(p => !isItemAbleToShow(p, scenario));
    this.resetDataSources();
  }

  private filterProviders(): void {
    this.listOfAllProviders.forEach(p => {
      p.item.belongs =
        (this.anyDepartmentsSelected || this.anySpecialtySelected)
        &&
        ((!!p.relatives.find(relative => relative.selected)) === (this.anyDepartmentsSelected || this.anySpecialtySelected))
        &&
        ((!p.relatives.find(relative => !relative.selected)) === (this.anyDepartmentsSelected && this.anySpecialtySelected));
    });
  }

  private filterSpecialties(): void {
    this.anySpecialtySelected = false;
    this.listOfAllSpecialties.forEach(s => {
      const relatedSelectedDepartment = s.relatives.find(relative => relative.selected
        && relative.item.nodePath.split('\\').length === 3);
      s.item.belongs = !this.anyDepartmentsSelected ? true : !!relatedSelectedDepartment;
      s.item.selected = !this.anyDepartmentsSelected ? false : !!relatedSelectedDepartment;
      if (s.item.selected) {
        this.anySpecialtySelected = true;
      }
    });
  }

  saveGroup(): void {
    if (!this.selectedProviders.length) {
      return;
    }
    const newFilters: FilterCriteria = {
      ...this.ngRedux.getState().filters,
      nodePath: calculateSelectedNodePath(this.ontologyListData?.providers.map(p => p.item) || [],
        this.ontologyListData?.allDepartmentsNodePath || '')
    };
    // TODO: fix this before enabling the updated filter banner component
    const saveDialogRef = this.dialog.open(UpdatedSaveGroupingComponent, {
      data: [newFilters, '']
    });
    saveDialogRef?.afterClosed().subscribe(result => {
      if (result.saved) {
        this.ngRedux.dispatch(filterCriteriaChangedTo(newFilters));
        this.ngRedux.dispatch(filterBannerEditChangedTo(false));
      }
    });
  }

  onSearchTextChanged(): void {
    this.listOfAllProviders.forEach(provider => {
      provider.item.matchesSearchText = this.searchText.length < 3 ||
        !!provider.item.item.providerNodeName?.toLowerCase().includes(this.searchText.toLowerCase());
    });
    this.showProviders();
  }

  clearSearch(): void {
    this.searchText = '';
    this.onSearchTextChanged();
  }

  handleScrollAvailableProviders(event: any): void {
    const table: any | undefined = event ? event.target : undefined;
    if (table && (table.scrollTop > 0.9 * (table.scrollHeight - table.clientHeight))) {
      this.NUMBER_AVAILABLE_PROVIDERS_TO_SHOW += 50;
      this.resetDataSources();
    }
  }

  handleScrollSelectedProviders(event: any): void {
    const table: any | undefined = event ? event.target : undefined;
    if (table && (table.scrollTop > 0.9 * (table.scrollHeight - table.clientHeight))) {
      this.NUMBER_SELECTED_PROVIDERS_TO_SHOW += 50;
      this.resetDataSources();
    }
  }

  handleSortChange(columnDef: string, listIndex: number): void {
    let order: SortingOrder | undefined;
    let list: RelatableSelectableItem<OntologyListEntity>[] = [];
    switch (listIndex) {
      case 0:
        list = this.availableProviders;
        switch (this.sortDirectionAvailableProviders) {
          case SortingOrder.ASCENDING:
            order = SortingOrder.DESCENDING;
            break;
          case SortingOrder.DESCENDING:
            order = undefined;
            break;
          case undefined:
            order = SortingOrder.ASCENDING;
            break;
        }
        this.sortDirectionAvailableProviders = order;
        this.sortColumnAvailableProviders = columnDef;
        break;
      case 1:
        list = this.selectedProviders;
        switch (this.sortDirectionSelectedProviders) {
          case SortingOrder.ASCENDING:
            order = SortingOrder.DESCENDING;
            break;
          case SortingOrder.DESCENDING:
            order = undefined;
            break;
          case undefined:
            order = SortingOrder.ASCENDING;
            break;
        }
        this.sortDirectionSelectedProviders = order;
        this.sortColumnSelectedProviders = columnDef;
    }
    if (order) {
      list.sort(function (a: RelatableSelectableItem<OntologyListEntity>,
                          b: RelatableSelectableItem<OntologyListEntity>) {
        return order === 'asc' ? (a.item.item[columnDef] || '').localeCompare(b.item.item[columnDef] || '')
          : order === 'desc' ? -1 * (a.item.item[columnDef] || '').localeCompare(
            b.item.item[columnDef] || '') : 0;
      });
      this.resetDataSources();
    }
  }

  clear(): void {
    if (!this.ontologyListData) {
      return;
    }
    this.selectedGroup = undefined;
    this.ontologyListData.providers.concat(this.ontologyListData.specialties).concat(this.ontologyListData.departments)
      .forEach(entry => {
        if (this.filterTab.newSelections) {
          if (entry.item.selected && entry.item.item.providerNodeName && entry.item.originallySelected) {
            this.filterTab.newSelections.deselections++;
          } else if (entry.item.selected && entry.item.item.providerNodeName && !entry.item.originallySelected) {
            this.filterTab.newSelections.selections--;
          }
        }
        entry.item.selected = false;
      });
    this.hiddenProviders = this.ontologyListData.providers;
    this.selectedProviders = [];
    this.availableProviders = [];
    this.searchDepartmentComponent?.ngOnInit();
    this.searchSpecialtyComponent?.ngOnInit();
    this.resetDataSources();
    if (this.onProvidersChanged) {
      this.onProvidersChanged(this.filterTab);
    }
  }

  selectAllProviders(event: MouseEvent): void {
    event.preventDefault();
    this.availableProviders.forEach(provider => {
      if (this.filterTab.newSelections) {
        if (!provider.item.originallySelected) {
          this.filterTab.newSelections.selections++;
        } else {
          this.filterTab.newSelections.deselections--;
        }
      }
      provider.item.selected = true;
    });
    this.selectedProviders = this.selectedProviders.concat(this.availableProviders);
    this.availableProviders = [];
    this.resetDataSources();
    if (this.onProvidersChanged) {
      this.onProvidersChanged(this.filterTab);
    }
    this.groupIsEdited = SearchProviderComponent.isGroupEdited(this.filterTab);
  }

  whenProviderSelected(provider: RelatableSelectableItem<OntologyListEntity>): void {
    if (this.filterTab.newSelections) {
      if (!provider.item.originallySelected) {
        this.filterTab.newSelections.selections++;
      } else {
        this.filterTab.newSelections.deselections--;
      }
    }
    provider.item.selected = true;
    this.availableProviders = this.availableProviders.filter(p => p !== provider);
    this.selectedProviders.push(provider);
    this.resetDataSources();
    if (this.onProvidersChanged) {
      this.onProvidersChanged(this.filterTab);
    }
    this.groupIsEdited = SearchProviderComponent.isGroupEdited(this.filterTab);
  }

  deselectAllProviders(event: MouseEvent): void {
    event.preventDefault();
    this.selectedProviders.forEach(provider => {
      if (this.filterTab.newSelections) {
        if (provider.item.originallySelected) {
          this.filterTab.newSelections.deselections++;
        } else {
          this.filterTab.newSelections.selections--;
        }
      }
      provider.item.selected = false;
    });
    this.availableProviders = this.availableProviders.concat(this.selectedProviders);
    this.selectedProviders = [];
    this.showProviders();
    if (this.onProvidersChanged) {
      this.onProvidersChanged(this.filterTab);
    }
    this.groupIsEdited = SearchProviderComponent.isGroupEdited(this.filterTab);
  }

  whenProviderDeselected(provider: RelatableSelectableItem<OntologyListEntity>): void {
    if (this.filterTab.newSelections) {
      if (provider.item.originallySelected) {
        this.filterTab.newSelections.deselections++;
      } else {
        this.filterTab.newSelections.selections--;
      }
    }
    provider.item.selected = false;
    this.selectedProviders = this.selectedProviders.filter(p => p !== provider);
    this.availableProviders.push(provider);
    this.showProviders();
    if (this.onProvidersChanged) {
      this.onProvidersChanged(this.filterTab);
    }
    this.groupIsEdited = SearchProviderComponent.isGroupEdited(this.filterTab);
  }

  whenDepartmentsToggled = (open: boolean) => {
    this.showDepartments = open;
    this.cdr.detectChanges();
  };

  whenSpecialtiesToggled = (open: boolean) => {
    this.showSpecialties = open;
    this.cdr.detectChanges();
  }

  whenDepartmentsUpdated(event: boolean): void {
    this.anyDepartmentsSelected = event;
    this.filterSpecialties();
    this.searchSpecialtyComponent?.ngOnInit();
    this.filterProviders();
    this.showProviders();
  }

  whenSpecialtiesUpdated(event: boolean): void {
    this.anySpecialtySelected = event;
    this.filterProviders();
    this.showProviders();
  }
}
